/**
 * What a picker value holds, field by field, as one owner for the row guard
 * and the submit validator.
 *
 * @packageDocumentation
 */

/** `string` must hold a string, `present` only needs the key, `optional` is not checked. */
export type FieldKind = 'string' | 'present' | 'optional';

/** The named keys of a type, without its index signature. */
export type KnownKeys<T> = keyof {
  [K in keyof T as string extends K
    ? never
    : number extends K
    ? never
    : K]: T[K];
};

/**
 * The field spec of a picker value type.
 *
 * @remarks
 * Every named key must be listed, and the kind follows from the type: an
 * optional key is `optional`, a required string is `string`, anything else
 * required is `present`. A key added to the interface therefore breaks the
 * build until the spec lists it.
 */
export type FieldSpec<T> = {
  readonly [K in KnownKeys<T>]-?: Record<never, never> extends Pick<T, K>
    ? 'optional'
    : T[K] extends string
    ? 'string'
    : 'present';
};

/**
 * Whether a row or a restored form value holds every required field of the
 * spec, with a non-blank `id` and `type`.
 *
 * @remarks
 * An `optional` field is skipped: neither its presence nor its value is
 * validated. Extra keys are allowed, because Resource Graph projects more than
 * the type names. The picker offers only rows that pass, and the validator refuses as
 * incomplete only values that fail, so the picker cannot write a value its
 * validator calls incomplete.
 *
 * @typeParam T - The picker value type the spec describes.
 * @param row - A Resource Graph row or a restored form value.
 * @param spec - The field spec of `T`.
 */
export function isPickerValue<T>(row: unknown, spec: FieldSpec<T>): row is T {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) {
    return false;
  }
  const value: Record<string, unknown> = { ...row };
  const fields: Readonly<Record<string, FieldKind>> = spec;
  return (
    Object.entries(fields).every(
      ([key, kind]) =>
        kind === 'optional' ||
        (Object.hasOwn(value, key) &&
          (kind === 'present' || typeof value[key] === 'string')),
    ) &&
    typeof value.id === 'string' &&
    value.id.trim() !== '' &&
    typeof value.type === 'string' &&
    value.type.trim() !== ''
  );
}
